import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { FadedPaper } from '../../theme/StyledFields';
import VizToggle, { VizType } from '../team-report/VizToggle';
import { SlidesSeenRow, CANONICAL_SLIDE_TITLES } from './types';

interface Props {
    rows: SlidesSeenRow[];
}

const SlidesSeenSection: React.FC<Props> = ({ rows }) => {
    const [viz, setViz] = useState<VizType>('bar');
    const theme = useTheme();

    const chartData = rows.map((r) => ({
        label: CANONICAL_SLIDE_TITLES[r.slide_number] || `Slide ${r.slide_number}`,
        count: r.count,
        uniqueLearners: r.unique_assignment_count,
        fill: theme.palette.primary.main,
    }));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-slides-seen">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">Slides Seen</Typography>
                <VizToggle value={viz} options={['bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Slide views by position across all sessions. Times seen = total views; Unique learners = distinct athletes who viewed.
            </Typography>

            {rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No slide view data available.
                </Typography>
            ) : (
                <>
                    {viz === 'bar' && (
                        <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 36)}>
                            <BarChart data={chartData} layout="vertical" margin={{ left: 120 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={110} />
                                <Tooltip
                                    content={({ active, payload }) =>
                                        active && payload?.[0] ? (
                                            <Box sx={{ p: 1.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                                <Typography variant="body2">
                                                    {payload[0].payload.label}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {payload[0].value} views · {payload[0].payload.uniqueLearners} learners
                                                </Typography>
                                            </Box>
                                        ) : null
                                    }
                                />
                                <Bar dataKey="count" name="Times seen" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]}>
                                    {chartData.map((entry, idx) => (
                                        <Cell key={idx} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {viz === 'table' && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Slide</TableCell>
                                        <TableCell>Title</TableCell>
                                        <TableCell align="right">Unique Learners</TableCell>
                                        <TableCell align="right">Times Seen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((r) => (
                                        <TableRow key={r.slide_number}>
                                            <TableCell>{r.slide_number}</TableCell>
                                            <TableCell>
                                                {CANONICAL_SLIDE_TITLES[r.slide_number] || `Slide ${r.slide_number}`}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                {r.unique_assignment_count}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                {r.count}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}
        </FadedPaper>
    );
};

export default SlidesSeenSection;
